// Path: frontend/src/pages/admin/components/chat-records/SalesChatRecordsTab.tsx

import React, { useState, useEffect } from 'react';
import {
  getUserSalesChatRecords,
  type UserSalesChatRecord
} from '../../../../lib/apis_chatmsg';
import { Loader2, MessageSquare, ChevronDown, ChevronRight, User } from 'lucide-react';
import SalesMessagesTable from './SalesMessagesTable';

const getTodayDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDateDaysAgo = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const SalesChatRecordsTab: React.FC = () => {
  const [startDate, setStartDate] = useState<string>(getDateDaysAgo(7));
  const [endDate, setEndDate] = useState<string>(getTodayDate());
  const [chatRecords, setChatRecords] = useState<UserSalesChatRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [expandedUsers, setExpandedUsers] = useState<Set<number>>(new Set());

  useEffect(() => {
    handleSearch();
  }, []);

  const handleSearch = async () => {
    setIsLoading(true);
    setHasSearched(true);
    try {
      const params: Record<string, any> = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const data = await getUserSalesChatRecords(params);
      setChatRecords(data);
      setExpandedUsers(new Set());
    } catch (error) {
      console.error('Failed to fetch sales chat records:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setStartDate(getDateDaysAgo(7));
    setEndDate(getTodayDate());
    setChatRecords([]);
    setHasSearched(false);
  };

  const toggleUser = (userId: number) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const totalMessages = chatRecords.reduce((sum, user) => sum + user.messages.length, 0);

  const renderUser = (user: UserSalesChatRecord) => {
    const isExpanded = expandedUsers.has(user.user_id);

    return (
      <div key={user.user_id} className="border border-gray-300 rounded-lg overflow-hidden">
        <div
          className="bg-gradient-to-r from-brand-50 to-cyan-50 p-4 cursor-pointer hover:from-brand-100 hover:to-cyan-100 transition-colors"
          onClick={() => toggleUser(user.user_id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              <div className="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
                <User size={20} className="text-brand-600" />
              </div>
              <div>
                <div className="font-semibold text-gray-800">{user.username}</div>
                <div className="text-xs text-gray-500">
                  {user.user_role} {user.user_memo && `· ${user.user_memo}`}
                </div>
                <div className="text-xs text-gray-400">
                  最新消息: {user.latest_message_time ? formatDate(user.latest_message_time) : '无'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="font-medium text-brand-600">{user.messages.length} 条消息</span>
            </div>
          </div>
        </div>
        {isExpanded && (
          <div className="p-0 bg-white">
            <SalesMessagesTable messages={user.messages} />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="font-medium text-gray-700">筛选条件</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">开始日期</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">结束日期</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              max={getTodayDate()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
            >
              {isLoading && <Loader2 size={16} className="animate-spin" />}
              查询
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              重置
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-brand-600 mr-2" />
          <span className="text-gray-500">加载聊天记录...</span>
        </div>
      ) : hasSearched ? (
        <>
          <div className="bg-gradient-to-r from-brand-600 to-cyan-600 rounded-lg shadow-lg p-4 mb-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare size={24} />
                <span className="text-lg font-semibold">查询结果</span>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <span>{chatRecords.length} 位用户</span>
                <span>{totalMessages} 条消息</span>
              </div>
            </div>
          </div>

          {chatRecords.length > 0 ? (
            <div className="space-y-4">
              {chatRecords.map(user => renderUser(user))}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
              <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">未找到符合条件的聊天记录</p>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
          <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">请设置筛选条件后点击查询按钮</p>
        </div>
      )}
    </>
  );
};

export default SalesChatRecordsTab;
